import React, { useEffect, useMemo } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import TextAlign from "@tiptap/extension-text-align"
import Placeholder from "@tiptap/extension-placeholder"
import { Btn } from "./ui" // adjust path if needed

function cx(...a) {
  return a.filter(Boolean).join(" ")
}

function ToolbarButton({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cx(
        "h-9 px-3 rounded-xl border text-sm transition",
        active ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 hover:border-neutral-900"
      )}
    >
      {children}
    </button>
  )
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Write...",
  openMedia, // optional: to insert images via your MediaLibraryModal
  className = "",
}) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    [placeholder]
  )

  const editor = useEditor({
    extensions,
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[180px] " +
          "prose-headings:font-semibold prose-a:text-cyan-700 prose-a:no-underline hover:prose-a:underline",
      },
    },
    onUpdate({ editor }) {
      const html = editor.getHTML()
      onChange?.(html)
    },
  })

  // keep editor in sync if external value changes (without cursor jumping too often)
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    const next = value || ""
    if (current !== next) editor.commands.setContent(next, false)
  }, [value, editor])

  if (!editor) return null

  const setLink = () => {
    const prev = editor.getAttributes("link")?.href || ""
    const url = window.prompt("Enter URL:", prev)
    if (url === null) return
    if (!url.trim()) {
      editor.chain().focus().unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run()
  }

  const insertImage = () => {
    if (!openMedia) {
      const url = window.prompt("Image URL:")
      if (url?.trim()) editor.chain().focus().setImage({ src: url.trim() }).run()
      return
    }

    // ✅ use your MediaLibraryModal:
    // we pass a ctx with an insert callback, then your onPickMedia will call it
    openMedia({
      mode: "richtext",
      kind: "richtext-image",
      multiple: false,
      insert: (url) => editor.chain().focus().setImage({ src: url }).run(),
    })
  }

  return (
    <div className={cx("rounded-3xl border border-neutral-200 bg-white overflow-hidden", className)}>
      {/* Toolbar */}
      <div className="p-2 border-b border-neutral-100 bg-neutral-50 flex flex-wrap gap-2">
        <ToolbarButton
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </ToolbarButton>

        <ToolbarButton
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </ToolbarButton>

        <ToolbarButton
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          U
        </ToolbarButton>

        <ToolbarButton
          title="H2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>

        <ToolbarButton
          title="H3"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>

        <ToolbarButton
          title="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToolbarButton>

        <ToolbarButton
          title="Ordered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToolbarButton>

        <ToolbarButton
          title="Quote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          “”
        </ToolbarButton>

        <ToolbarButton title="Link" active={editor.isActive("link")} onClick={setLink}>
          Link
        </ToolbarButton>

        <ToolbarButton title="Unlink" active={false} onClick={() => editor.chain().focus().unsetLink().run()}>
          Unlink
        </ToolbarButton>

        <ToolbarButton
          title="Align left"
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          Left
        </ToolbarButton>

        <ToolbarButton
          title="Align center"
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          Center
        </ToolbarButton>

        <ToolbarButton
          title="Align right"
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          Right
        </ToolbarButton>

        <ToolbarButton title="Image" active={false} onClick={insertImage}>
          Image
        </ToolbarButton>

        <div className="flex-1" />

        <Btn
          variant="ghost"
          className="h-9 px-3"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          Undo
        </Btn>

        <Btn
          variant="ghost"
          className="h-9 px-3"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          Redo
        </Btn>
      </div>

      {/* Editor */}
      <div className="p-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
